import { useSessionStorage } from "@shared/frontend/hooks/use-storage.hook";
import { Roles } from "@shared/Roles";
import { linko } from "@shared/linko";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { remult } from "remult";
import SummerCampaignLeadsTable from "../summer-campaign-leads-table";
import TeamsTable from "./teams/TeamsTable";
import VolunteersTable from "./volunteers/VolunteersTable";

const USERS_TABLE_TAB_KEY = "users_table_tab";

export type ActiveTab =
  | "volunteers"
  | "teams"
  | "history"
  | "once"
  | "summerLeads";

export interface Tabs {
  activeTab: ActiveTab | null;
  setActiveTab?: React.Dispatch<React.SetStateAction<ActiveTab | null>>;
  options?: { content: string | React.JSX.Element; value: ActiveTab }[];
}

const UsersTables: React.FC = () => {
  const { state } = useLocation();
  const { table: tableName } = state ?? {};
  const { associationId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useSessionStorage<ActiveTab | null>(
    USERS_TABLE_TAB_KEY,
    () => tableName,
  );

  useEffect(() => {
    if (tableName && tableName !== activeTab) {
      setActiveTab(tableName);
    }
  }, [tableName]);

  useEffect(() => {
    if (associationId && activeTab !== "volunteers") {
      setActiveTab("volunteers");
    }
  }, [associationId]);

  const handleSetActiveTab: React.Dispatch<
    React.SetStateAction<ActiveTab | null>
  > = (tab) => {
    const newTab = typeof tab === "function" ? tab(activeTab) : tab;
    setActiveTab(newTab);
    if (associationId) {
      navigate("/volunteers");
    }
  };

  const { data: pendingLeadsCount = 0 } = useQuery({
    queryKey: ["summer-campaign-leads-pending-count"],
    queryFn: async () => {
      const { data } = await linko.summerCampaignLead.queries.getPendingCount();
      return data;
    },
    enabled: remult.isAllowed(Roles.AdminsAndManager),
    staleTime: 1000 * 60 * 2,
  });

  const isTeams = activeTab === "teams";
  const isSummerLeads = activeTab === "summerLeads";

  const options: Tabs["options"] = [
    {
      content: "מתנדבים",
      value: "volunteers",
    },
    {
      content: "היסטוריה",
      value: "history",
    },
    {
      content: "פרויקטים",
      value: "teams",
    },
    {
      content:
        pendingLeadsCount > 0
          ? `לידים (${pendingLeadsCount})`
          : "לידים",
      value: "summerLeads",
    },
  ];
  const tabsProps = { activeTab, setActiveTab: handleSetActiveTab, options };

  if (isSummerLeads) {
    return <SummerCampaignLeadsTable {...tabsProps} />;
  }

  return isTeams ? (
    <TeamsTable {...tabsProps} />
  ) : (
    <VolunteersTable {...tabsProps} />
  );
};

export default UsersTables;
